// utils/searchDynamic.js
import { SEARCH_CONFIG } from "config/searchConfig";

// helpers
const asValues = (arr) =>
  (arr || [])
    .map((o) => (typeof o === "string" ? o : o?.value))
    .filter(Boolean);

const encoders = {
  array: (v) => v,
  csv: (v) => (Array.isArray(v) ? v.join(",") : String(v ?? "")),
  boolInt: (v) => (v ? 1 : 0),
  bool: (v) => !!v,
  number: (v) => (v === "" || v == null ? null : Number(v)),
  string: (v) => (v == null ? "" : String(v)),
};

function defaultEncoderForType(type) {
  switch (type) {
    case "multi":
      return "array";
    case "boolean":
      return "bool";
    case "number":
      return "number";
    default:
      return "string";
  }
}

function coerceForType(type, value) {
  switch (type) {
    case "multi":
      return asValues(value);
    case "boolean":
      return !!value;
    case "number":
      return value === "" || value == null ? null : Number(value);
    default:
      return value ?? "";
  }
}

function equalsDefault(field, value) {
  const d = field.default;
  if (field.type === "multi") return (value?.length || 0) === (d?.length || 0);
  if (field.type === "boolean") return !!value === !!d;
  return (value ?? "") === (d ?? "");
}

/**
 * Build dynamic helpers from SEARCH_CONFIG for a namespace.
 */
export function makeSearchHelpers(namespace) {
  const cfg = SEARCH_CONFIG[namespace];
  if (!cfg)
    throw new Error(`Unknown namespace "${namespace}" in SEARCH_CONFIG`);

  const fields = cfg.fields || [];

  /**
   * Normalize raw filters (from context/SearchPanel) to typed values.
   */
  function normalize(raw) {
    const out = {};
    for (const f of fields) {
      const v = raw?.[f.key];
      out[f.key] = coerceForType(f.type, v);
    }
    return out;
  }

  /**
   * Build stable query key (sort arrays; skip defaults if desired).
   */
  function keyFrom(raw, { pruneDefaults = false } = {}) {
    const f = normalize(raw);
    const keyObj = {};
    for (const field of fields) {
      let v = f[field.key];
      if (field.type === "multi") v = [...(v || [])].sort();
      if (pruneDefaults && equalsDefault(field, v)) continue;
      keyObj[field.key] = v;
    }
    return JSON.stringify(keyObj);
  }

  /**
   * Convert normalized filters to API params.
   * - include/exclude lets you tailor per-endpoint
   * - pruneDefaults removes fields equal to defaults
   */
  function toApiParams(
    raw,
    {
      include, // array of keys to include
      exclude, // array of keys to exclude
      pruneDefaults = false,
    } = {}
  ) {
    const f = normalize(raw);
    const params = {};
    const includeSet = include ? new Set(include) : null;
    const excludeSet = exclude ? new Set(exclude) : null;

    for (const field of fields) {
      if (includeSet && !includeSet.has(field.key)) continue;
      if (excludeSet && excludeSet.has(field.key)) continue;

      const v = f[field.key];
      if (pruneDefaults && equalsDefault(field, v)) continue;

      const apiName = field.apiName || field.key;
      const encodeName = field.encode || defaultEncoderForType(field.type);
      const encodeFn = encoders[encodeName] || ((x) => x);

      params[apiName] = encodeFn(v);
    }
    return params;
  }

  return { fields, normalize, keyFrom, toApiParams };
}
